import React, { useState } from 'react';
import './TripCard.css';
import { Icons } from '../data/Icons';
import { Dialog } from '@mui/material';
import ProfilePage from '../Pages/Profile';
import ClickableParticipant from './ClickableParticipant';
import ProfileDialog from "./ProfileDialog";
import { deleteDoc, doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { useUser } from '../contexts/UserProvider';
import Swal from 'sweetalert2';
import { sendGroupJoinRequest } from '../firebase/services/JoinRequests';

const TripDetailsModal = ({ isOpen, onClose, trip, isJoining, compact = false, hideCancelButton = false, onAfterCancel, hideMoreInfo = false }) => {
    const { profile } = useUser();
    const [isUserOpen, setIsUserOpen] = useState(false);
    const [userId, setUserId] = useState(0);

    if (!isOpen) return null;

    const handleOpenUser = () => {
        setIsUserOpen(true);
    };

    const handleCloseUser = () => {
        setIsUserOpen(false);
    };

    // Format date to dd/mm/yyyy
    const formatDate = (date) => {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    // Format time to hh:mm
    const formatTime = (date) => {
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
    };

    const tripDateObj = trip.timestamp?.toDate?.() ?? new Date();
    const isFutureTrip = tripDateObj > new Date();

    const handleCancelParticipation = () => {
        console.log('Cancel button clicked');
        if (!isJoining) {
            const docRef = doc(db, 'tripEvents', trip.id);
            console.log('Getting trip doc:', trip.id);
            getDoc(docRef).then(realTripRef => {
                console.log('Got doc:', realTripRef.exists());
                if (realTripRef.exists()) {
                    const tripData = realTripRef.data();
                    console.log('Trip data:', tripData);
                    if (tripData.participants.length === 2) {
                        console.log('Deleting trip...');
                        deleteDoc(docRef).then(() => {
                            console.log('Trip deleted');
                            Swal.fire({
                                title: "טיול נמחק!",
                                icon: "success"
                            });
                            if (typeof onAfterCancel === 'function') onAfterCancel();
                            if (typeof onClose === 'function') onClose(true);
                        }).catch(e => console.error('Delete error:', e));
                    }
                    else {
                        tripData.participants = tripData.participants.filter((participant) => participant !== profile.id);
                        console.log('Updating trip participants:', tripData.participants);
                        setDoc(docRef, tripData).then(() => {
                            console.log('Trip updated');
                            Swal.fire({
                                title: "ההצטרפות בוטלה",
                                icon: "success"
                            });
                            if (typeof onAfterCancel === 'function') onAfterCancel();
                            if (typeof onClose === 'function') onClose(true);
                        }).catch(e => console.error('Update error:', e));
                    }
                } else {
                    console.log('Trip doc does not exist');
                }
            }).catch(e => console.error('getDoc error:', e));
        }
    };

    return (
        <div dir="rtl">
            <div className="modal-overlay-TripCard">
                <div className="modal-content-TripCard">
                    <div className="modal-scroll">
                        <button className="close-button-TripCard" onClick={onClose}>✖</button>

                        <div className="trip-text">
                            {/* Trip main details */}
                            <div className="trip-header-layout">
                                <img src={trip.image} alt={trip.title} className="trip-main-img" />
                                <div className="trip-info">
                                    <h2>{trip.title}</h2>

                                    <div className="trip-data">
                                        <p><span className="trip-category">{Icons.style}סגנון:</span> {trip.style}</p>

                                        {!compact && (
                                            <p>
                                                <span className="trip-category">{Icons.date}מתי:</span>
                                                {formatDate(tripDateObj)}, בשעה {formatTime(tripDateObj)}
                                            </p>
                                        )}

                                        <p><span className="trip-category">{Icons.suitableFor}מתאים ל:</span> {trip.forWhom}</p>

                                        <p>
                                            <span className="trip-category">{Icons.difficulty}רמת קושי:</span> {trip.difficulty} |
                                            <span className="trip-category">{Icons.duration}משך:</span> {trip.duration} |
                                            <span className="trip-category">{Icons.location}אזור:</span> {trip.location}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="trip-body">
                                <div className="trip-section">
                                    <h4>תיאור הטיול:</h4>
                                    <p>{trip.description}</p>
                                </div>

                                <div className="trip-section">
                                    <h4>מה להביא:</h4>
                                    <p>{trip.equipment}</p>
                                </div>

                                {!compact && (
                                    <div className="trip-section">
                                        <h4>משתתפים:</h4>
                                        <div className="participants">
                                            {trip.participants.map((id, idx) => (
                                                <ClickableParticipant id={id} key={idx} onClick={() => {
                                                    setUserId(id);
                                                    handleOpenUser();
                                                }} />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="trip-buttons">
                            <div className="modal-button-wrapper">
                                {isFutureTrip && !hideCancelButton ? (
                                    isJoining ? (
                                        // ✅ When user clicks "Join Request" on a group trip
                                        <button
                                            className="button-TripCard join"
                                            onClick={async () => {
                                                try {
                                                    console.log("שולח בקשת הצטרפות לטיול עם tripId:", trip.id);
                                                    await sendGroupJoinRequest({
                                                        fromUserId: profile.id,
                                                        toUserId: trip.createdBy,
                                                        tripId: trip.id,
                                                    });
                                                    Swal.fire({
                                                        title: "הבקשה נשלחה",
                                                        icon: "success"
                                                    });
                                                    onClose();
                                                } catch (error) {
                                                    console.error("שגיאה בשליחת הבקשה:", error);
                                                    Swal.fire({
                                                        title: "אירעה שגיאה",
                                                        text: "אנא נסה שוב מאוחר יותר",
                                                        icon: "error"
                                                    });
                                                }
                                            }}
                                        >
                                            בקש להצטרף
                                        </button>
                                    ) : (
                                        // ❌ Cancel join
                                        <button
                                            className="button-TripCard cancel"
                                            onClick={() => {
                                                const docRef = doc(db, 'tripEvents', trip.id);
                                                getDoc(docRef).then(realTripRef => {
                                                    if (realTripRef.exists()) {
                                                        const tripData = realTripRef.data();
                                                        if (tripData.participants.length === 2) {
                                                            deleteDoc(docRef).then(() => {
                                                                Swal.fire({
                                                                    title: "טיול נמחק!",
                                                                    icon: "success"
                                                                });
                                                                onClose();
                                                            });
                                                        } else {
                                                            tripData.participants = tripData.participants.filter((participant) => participant !== profile.id);
                                                            tripData.createdBy = tripData.participants[0];
                                                            setDoc(docRef, tripData).then(() => {
                                                                Swal.fire({
                                                                    title: "ההצטרפות בוטלה",
                                                                    icon: "success"
                                                                });
                                                                onClose();
                                                            });
                                                        }
                                                    }
                                                });
                                            }}
                                        >
                                            בטל הצטרפות
                                        </button>
                                    )
                                ) : (
                                    isFutureTrip && trip.moreInfoUrl && !hideMoreInfo && (
                                        <a
                                            className="button-TripCard join"
                                            href={trip.moreInfoUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            למידע נוסף
                                        </a>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ProfileDialog profileId={userId} open={isUserOpen} onClose={handleCloseUser} />

        </div>
    );
};

export default TripDetailsModal;
